import { excludeNullOrUndefined, getBreakdownTitle } from 'checkout/utils'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { isCruiseItem } from 'lib/checkout'
import createSelector from 'lib/createSelector'
import { getCruiseDurationByItinerary } from 'lib/Cruise/cruiseUtils'
import { pluralizeToString } from 'lib/pluralize'
import moment from 'moment'
import { arrayResultMemoizeOptions } from 'checkout/lib/utils/misc/arrayResultMemoizeOptions'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { getDepositServiceFeeConfig } from 'checkout/selectors/featureConfig/deposit'
import { DepositType } from 'constants/checkout'
import { buildCruiseItemView } from 'checkout/lib/utils/cruises/view'
import { buildCruiseSummaryBreakdown } from 'checkout/lib/utils/cruises/summary'
import { getCheckoutRouteFromURL, getIsCabinRateStep } from 'checkout/lib/utils/cruises/steps'
import { getCruiseCountriesByItinerary, getCruisePortsByItinerary } from 'lib/Cruise/cruiseUtils'

export const getCruiseItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.CruiseItem> => items.filter(isCruiseItem),
  arrayResultMemoizeOptions,
)

export function getCruiseBreakdownView(
  offerView: App.Checkout.CruiseAccommodationOfferView,
  hidePrice: boolean,
): App.Checkout.PriceBreakdownView {
  const { startDate, endDate, saleUnit, itemViews, mainLabel } = offerView

  const cruiseDuration = getCruiseDurationByItinerary(offerView.offer?.itinerary ?? [])

  const items = itemViews.map<App.Checkout.ItemBreakdownView>(
    (itemView: App.Checkout.CruiseAccommodationItemView, index) => {
      const { additionalElements, additionalInfoText } = buildCruiseSummaryBreakdown(itemView)

      const onboardCreditAmount = itemView.cabinRateView?.onboardCredits?.amount || itemView.cabinHold?.pricing?.onboardCredits?.amount
      const onboardCreditCurrencyCode = itemView.cabinRateView?.onboardCredits?.currencyCode || itemView.cabinHold?.pricing?.onboardCredits?.currencyCode
      const hasOnboardCredits = !!(onboardCreditAmount && onboardCreditCurrencyCode)

      const price = (hidePrice || !window.location.pathname.includes('purchase')) ? undefined : (itemView?.price ?? 0) - itemView.taxesAndFees
      const title = [`${saleUnit} ${index + 1}`, itemView.cabinCategory?.cabinType].filter(Boolean).join(' • ')

      return {
        title,
        additionalInfoText,
        cancellationPolicy: itemView.cabinHold?.pricing.cancellationPolicy,
        additionalElements,
        price,
        taxesAndFees: itemView.taxesAndFees,
        offerId: itemView.item.offerId,
        itemType: 'cruise',
        ...(hasOnboardCredits && {
          onboardCredits: {
            amount: onboardCreditAmount,
            currencyCode: onboardCreditCurrencyCode,
          },
        }),
      }
    })

  const formattedStartDate = startDate ? `${moment(startDate).format(DMY_CASUAL_FORMAT)}` : 'start'
  const formattedEndDate = endDate ? `${moment(endDate).format(DMY_CASUAL_FORMAT)}` : 'end'

  return {
    title: getBreakdownTitle(offerView),
    description: mainLabel,
    additionalInfoText: [
      `${offerView.offer?.cruiseLine.name} • ${offerView.offer?.ship.name}`,
      `${formattedStartDate} - ${formattedEndDate} • ${pluralizeToString('night', cruiseDuration - 1)}`,
    ],
    items,
    price: 0, // hide package price in payment summary
    onboardCredits: items.find(item => item.onboardCredits)?.onboardCredits,
  }
}

export const getCheckoutCruiseOfferView = createSelector(
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.cruiseOffers,
  (state: App.State) => state.cruise.cabinRates,
  (state: App.State) => state.cruise.cabinRateDetails,
  (state: App.State) => state.cruise.cruiseDeparture,
  (state: App.State) => state.cruise.cabinAvailability,
  (state: App.State) => state.cruise.mapLegend,
  (state: App.State) => state.cruise.deckSelections,
  (state: App.State) => state.cruise.cabinHold,
  getDepositServiceFeeConfig,
  (state: App.State) => state.cruise.cabinPricing,
  (state: App.State) => state.router.location.pathname,
  (
    items,
    offers,
    rates,
    rateDetails,
    departures,
    availableCabinList,
    legendList,
    deckSelections,
    cabinHold,
    serviceFee,
    cabinPricing,
    pathname,
  ): App.WithDataStatus<Array<App.Checkout.CruiseAccommodationOfferView>> => {
    if (items.length === 0) { return { hasRequiredData: true, data: [] } }
    // Assumption: Only 1 cruise item per cart
    const item = items[0]

    const offer = offers[item.offerId]
    const rate = rates ? rates.find(rate => rate.componentId === item.componentId) : undefined

    // Why do schedules have null 'id'?
    // I have to search by 'name'
    const depositAmount = cabinPricing?.paymentScheduleDetails?.schedule?.firstPayment?.amount ?? null

    // Assumption: Only 1 cabin per cruise item
    const details = rateDetails?.[item.componentId || '']
    const itemViewWithStatus = buildCruiseItemView(item, offer, rate, cabinHold, details, cabinPricing)

    const cruiseCheckoutStep = getCheckoutRouteFromURL(pathname)
    const isCabinRateStep = getIsCabinRateStep(cruiseCheckoutStep)

    // The data required is different according to the checkout stage
    const hasRequiredData = isCabinRateStep ? !!offer : [
      itemViewWithStatus.hasRequiredData,
      !!availableCabinList && availableCabinList.length > 0,
      !!offer,
      !!rate,
    ].every(Boolean)

    // 'duration' and 'durationLabel' can be different
    const dayDuration = getCruiseDurationByItinerary(offer?.itinerary)
    const countriesCount = getCruiseCountriesByItinerary(offer?.itinerary)
    const portsCount = getCruisePortsByItinerary(offer?.itinerary)

    const offerView: App.Checkout.CruiseAccommodationOfferView = {
      offer,
      offerId: item.offerId,
      mainLabel: offer?.name,
      duration: offer?.duration,
      startDate: departures?.[item.departureId]?.departureDate,
      endDate: departures?.[item.departureId]?.returnDate,
      image: offer?.images?.[0],
      location: getLocationString(offer),
      occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
      offerType: offer?.type,
      reservationType: 'instant_booking',
      saleUnit: 'cabin',
      durationLabel: pluralizeToString('night', dayDuration - 1),
      offerLoaded: !!offer,
      itemViews: [itemViewWithStatus.data],
      propertyTimezone: 'GMT',
      operatorName: offer ? `Operated by: ${offer.cruiseLine.name}` : '',
      shipName: offer?.ship ? `Cruise ship: ${offer.ship.name}` : '',
      countries: countriesCount,
      ports: portsCount,
      ...(depositAmount && { depositAmount }),
      serviceFee,
      depositType: DepositType.FLAT,
      designation: 'Cruise',
      // bookingInfo: bookingInfo?.[bookingId],
      ship: offer?.ship,
      availableCabinList: availableCabinList ?? [],
      legendList,
      deckSelections: deckSelections ?? {},

      // accommodation fields
      // Should 'cruise' really inherit 'baseAccommodation'?
      urgencyLabels: [],
      bedGroups: [],
      confidenceLabels: [],
    }

    return {
      hasRequiredData,
      data: [offerView],
    }
  },
)
